import { useCallback, useEffect } from 'react';
import { browserStorageKeys } from '../../constants/browserStorageKeys/browserStorageKeys.constant';
import { MachineApprovalStatus } from '../../constants/machine/machine.constant';
import { browserStorage } from '../../utils/browserStorage.util';
import { mapRegionToCustomCode, UserRegionType } from '../../utils/mapper/mapRegionToCustomCode';
import { useGetMyPopularTractor } from '../api/user/queries/useGetMyPopularTractor';
import { useCheckUserLoggedIn } from '../user/useCheckUserLoggedIn';
import { useGetUserProfile } from '../user/useGetUserProfile';
import { useGetUserActivityTracking } from '../api/user/queries/useGetUserActivityTracking.query';
import { useGetUserOwnTractorPokesMeCount } from '../api/user/queries/useGetUserOwnTractorPokesMeCount.query';
import { useGetMyOwnTractorImageExists } from '../api/user/queries/useGetMyOwnTractorImageExists.query';

const storageType = 'localStorage';

const TRUE = 'true';
const FALSE = 'false';
const NONE = 'none';

export const useUpdateBrowserStorage = () => {
  useUpdateUserLoggedIn();
  useUpdateUserHasNewPoke({ shouldFetch: true });
  useUpdateUserHasOwnTractor();
  useUpdateUserRegionDepth1();
  useUpdateUserPageVisitedCount();
  useUpdateUserExecutedPokeCount();
  useUpdateUserOwnTractorImageExists();
  useUpdateUserAdvertisementTermsAgreement();
};

const useUpdateUserLoggedIn = () => {
  const { isUserLoggedIn } = useCheckUserLoggedIn();

  useEffect(() => {
    const itemValue = isUserLoggedIn ? TRUE : FALSE;
    browserStorage.setItem({ storageType, itemKey: browserStorageKeys.login, value: itemValue });
  }, [isUserLoggedIn]);
};

interface IUseUpdateUserHasNewPoke {
  shouldFetch: boolean;
}

export const useUpdateUserHasNewPoke = ({ shouldFetch }: IUseUpdateUserHasNewPoke) => {
  const itemKey = browserStorageKeys.hasNewPoke;

  const { APPROVAL, PSWAITING } = MachineApprovalStatus;
  const { data: publicTractor } = useGetMyPopularTractor({
    searchParam: { machineApprovalStatus: `${APPROVAL},${PSWAITING}`, isPokeEnabled: true },
    options: {
      enabled: shouldFetch,
    },
  });

  const updateStorage = useCallback(() => {
    if (publicTractor && publicTractor.isPokeNotificationActive) {
      browserStorage.setItem({ storageType, itemKey, value: TRUE });
    } else {
      browserStorage.setItem({ storageType, itemKey, value: FALSE });
    }
  }, [publicTractor, itemKey]);

  useEffect(() => {
    updateStorage();
  }, [updateStorage]);

  return updateStorage;
};

const useUpdateUserHasOwnTractor = () => {
  const itemKey = browserStorageKeys.hasOwnTractor;

  const { APPROVAL, PSWAITING, WAITING, REJECT } = MachineApprovalStatus;
  const { data: ownTractor } = useGetMyPopularTractor({
    searchParam: { machineApprovalStatus: `${APPROVAL},${PSWAITING},${WAITING},${REJECT}` },
  });

  useEffect(() => {
    if (ownTractor) {
      browserStorage.setItem({ storageType, itemKey, value: TRUE });
    } else {
      browserStorage.setItem({ storageType, itemKey, value: FALSE });
    }
  }, [ownTractor, itemKey]);
};

const useUpdateUserRegionDepth1 = () => {
  const itemKey = browserStorageKeys.urd1;

  const { userProfile } = useGetUserProfile();

  useEffect(() => {
    if (userProfile && userProfile.regionDepth1) {
      const itemValue = mapRegionToCustomCode({ regionDepth1: userProfile.regionDepth1 as UserRegionType });
      browserStorage.setItem({
        storageType,
        itemKey,
        value: itemValue,
      });
    } else {
      browserStorage.setItem({ storageType, itemKey, value: NONE });
    }
  }, [userProfile, itemKey]);
};

const useUpdateUserPageVisitedCount = () => {
  const { visitedCountTLP: itemKeyTLP, visitedCountTDP: itemKeyTDP, visitedCountPDP: itemKeyPDP } = browserStorageKeys;

  const { data: userActivityTracking } = useGetUserActivityTracking();

  useEffect(() => {
    if (!userActivityTracking || userActivityTracking.length === 0) return;

    const visitedCountTLP = userActivityTracking?.find(
      (activity) => activity.activityType === 'pokeListPageVisitedCount',
    )?.value;

    if (visitedCountTLP !== undefined) {
      browserStorage.setItem({ storageType, itemKey: itemKeyTLP, value: String(visitedCountTLP) });
    }

    const visitedCountTDP = userActivityTracking?.find(
      (activity) => activity.activityType === 'pokeDetailPageVisitedCount',
    )?.value;

    if (visitedCountTDP !== undefined) {
      browserStorage.setItem({ storageType, itemKey: itemKeyTDP, value: String(visitedCountTDP) });
    }

    const visitedCountPDP = userActivityTracking?.find(
      (activity) => activity.activityType === 'productDetailPageVisitedCount',
    )?.value;

    if (visitedCountPDP !== undefined) {
      browserStorage.setItem({ storageType, itemKey: itemKeyPDP, value: String(visitedCountPDP) });
    }
  }, [userActivityTracking, itemKeyTLP, itemKeyTDP, itemKeyPDP]);
};

const useUpdateUserExecutedPokeCount = () => {
  const itemKey = browserStorageKeys.executedPokeCount;

  const { data: userOwnTractorPokesMeCount } = useGetUserOwnTractorPokesMeCount();

  useEffect(() => {
    if (userOwnTractorPokesMeCount !== undefined) {
      browserStorage.setItem({ storageType, itemKey, value: String(userOwnTractorPokesMeCount) });
    }
  }, [userOwnTractorPokesMeCount, itemKey]);
};

const useUpdateUserOwnTractorImageExists = () => {
  const itemKey = browserStorageKeys.userOwnTractorImageExists;

  const { data: userOwnTractorImageExists } = useGetMyOwnTractorImageExists();

  useEffect(() => {
    if (userOwnTractorImageExists !== undefined) {
      browserStorage.setItem({ storageType, itemKey, value: String(userOwnTractorImageExists) });
    }
  }, [userOwnTractorImageExists, itemKey]);
};

const useUpdateUserAdvertisementTermsAgreement = () => {
  const itemKey = browserStorageKeys.advertisementTermsAgreement;

  const { userProfile } = useGetUserProfile();

  useEffect(() => {
    if (userProfile && userProfile.advertisementTermsAgreement) {
      browserStorage.setItem({
        storageType,
        itemKey,
        value: TRUE,
      });
    } else {
      browserStorage.setItem({ storageType, itemKey, value: FALSE });
    }
  }, [userProfile, itemKey]);
};
